import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { Checkbox, FormControlLabel, MenuItem, TextField } from "@mui/material";
import { Button } from "@src/shared/components/Button/Button";
import { useSendProductReview } from "@src/entities/feedback";

import {
  DesiredImprovement,
  FeedbackFormValues,
  UsefulFeature,
} from "../model/types";
import { useNavigate } from "react-router-dom";
import StarIcon from "@src/shared/assets/rating-star.svg?react";

import styles from "./FeedbackPage.module.scss";

const usefulFeatureOptions: {
  value: UsefulFeature;
  label: string;
}[] = [
  // { value: "ranking", label: "Ранжирование" },
  // { value: "hh_search", label: "Поиск на hh.ru" },
  // { value: "flexible_filters", label: "Гибкие фильтры" },
  // { value: "other", label: "Другое" },
  { value: "vacancy_analysis", label: "Автоматический анализ вакансии" },
  { value: "relevant_candidates", label: "Подбор наиболее релевантных кандидатов" },
  { value: "ai_evaluation", label: "AI-оценка и объяснение результатов" },
  { value: "candidate_card", label: "Карточка кандидата" },
  // { value: "other", label: "Другое" },
];

const improvementOptions: {
  value: DesiredImprovement;
  label: string;
}[] = [
  { value: "more_filters", label: "Больше фильтров" },
  { value: "export_results", label: "Экспорт результатов" },
  { value: "other_sites", label: "Поддержка других сайтов" },
  { value: "ats_integration", label: "Интеграция с ATS" },
  { value: "other", label: "Другое" },
];

const defaultValues: FeedbackFormValues = {
  overallSatisfaction: 0,
  usefulFeatures: [],
  interfaceClarity: 0,
  desiredImprovements: [],
  speedRating: 0,
  hasBlockers: "",
  blockerText: "",
  recommendScore: 0,
  comment: "",
};

interface StarRatingProps {
  value: number;
  max?: number;
  disabled?: boolean;
  error?: boolean;
  onChange: (value: number) => void;
}

const StarRating = ({
  value,
  max = 5,
  disabled = false,
  error = false,
  onChange,
}: StarRatingProps) => {
  return (
    <div
      className={`${styles.stars} ${error ? styles.starsError : ""} ${
        disabled ? styles.starsDisabled : ""
      }`}
    >
      {Array.from({ length: max }, (_, index) => {
        const starValue = index + 1;
        const isActive = starValue <= value;

        return (
          <button
            key={starValue}
            type="button"
            className={`${styles.star} ${isActive ? styles.starActive : ""}`}
            disabled={disabled}
            onClick={() => onChange(value === starValue ? 0 : starValue)}
            aria-label={`Оценка ${starValue} из ${max}`}
          >
            <StarIcon className={styles.starIcon} />
          </button>
        );
      })}
    </div>
  );
};

interface CheckboxGroupProps<T extends string> {
  value: T[];
  options: {
    value: T;
    label: string;
  }[];
  onChange: (value: T[]) => void;
}

const CheckboxGroup = <T extends string>({
  value,
  options,
  onChange,
}: CheckboxGroupProps<T>) => {
  const handleChange = (optionValue: T) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((item) => item !== optionValue));
      return;
    }

    onChange([...value, optionValue]);
  };

  return (
    <div className={styles.checkboxGroup}>
      {options.map((option) => (
        <FormControlLabel
          key={option.value}
          className={styles.checkboxLabel}
          control={
            <Checkbox
              checked={value.includes(option.value)}
              onChange={() => handleChange(option.value)}
              sx={{
                color: "#A4BBC7",
                padding: "4px 8px 4px 0",
                "&.Mui-checked": {
                  color: "#00C195",
                },
              }}
            />
          }
          label={option.label}
        />
      ))}
    </div>
  );
};

export const FeedbackPage = () => {
  const { mutateAsync: sendProductReview, isPending } = useSendProductReview();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FeedbackFormValues>({
    defaultValues,
    mode: "onSubmit",
  });

  const hasBlockers = watch("hasBlockers");
  const isBlockerTextDisabled = hasBlockers !== "yes";

  useEffect(() => {
    if (hasBlockers !== "yes") {
      setValue("blockerText", "");
    }
  }, [hasBlockers, setValue]);

  const onSubmit = async (values: FeedbackFormValues) => {
    await sendProductReview(
      {
        overall_satisfaction: values.overallSatisfaction,
        useful_features: values.usefulFeatures,
        interface_clarity: values.interfaceClarity,
        desired_improvements: values.desiredImprovements,
        speed_rating: values.speedRating,
        has_blockers: values.hasBlockers === "yes",
        blockers_text: values.blockerText.trim() || null,
        recommend_score: values.recommendScore,
        comment: values.comment.trim() || null,
      },
      {
        onSuccess: () => {
          navigate("/");
        },
      },
    );

    reset(defaultValues);
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h1 className={styles.title}>
          Опрос удовлетворенности работой сервиса
        </h1>
        <p className={styles.subtitle}>
          Заполните опрос, нам важно ваше мнение!
        </p>
      </div>

      <form className={styles.card} onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.grid}>
          <section className={styles.column}>
            <div className={styles.question}>
              <div className={styles.questionHeader}>
                <h3>Насколько вы довольны Наймусом в целом?</h3>

                {errors.overallSatisfaction && (
                  <span className={styles.errorText}>Поставьте оценку</span>
                )}
              </div>

              <Controller
                name="overallSatisfaction"
                control={control}
                rules={{
                  validate: (value) => value > 0 || "Поставьте оценку",
                }}
                render={({ field }) => (
                  <StarRating
                    value={field.value}
                    max={5}
                    error={!!errors.overallSatisfaction}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>

            <div className={styles.question}>
              <h3>Что из функциональности оказалось для вас самым полезным?</h3>

              <Controller
                name="usefulFeatures"
                control={control}
                render={({ field }) => (
                  <CheckboxGroup
                    value={field.value}
                    options={usefulFeatureOptions}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>

            <div className={styles.question}>
              <div className={styles.questionHeader}>
                <h3>Насколько понятен интерфейс и логика работы сервиса?</h3>

                {errors.interfaceClarity && (
                  <span className={styles.errorText}>Поставьте оценку</span>
                )}
              </div>

              <Controller
                name="interfaceClarity"
                control={control}
                rules={{
                  validate: (value) => value > 0 || "Поставьте оценку",
                }}
                render={({ field }) => (
                  <StarRating
                    value={field.value}
                    max={5}
                    error={!!errors.interfaceClarity}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>

            <div className={styles.question}>
              <h3>Какие доработки были бы для вас полезны?</h3>

              <Controller
                name="desiredImprovements"
                control={control}
                render={({ field }) => (
                  <CheckboxGroup
                    value={field.value}
                    options={improvementOptions}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          </section>

          <section className={styles.column}>
            <div className={styles.question}>
              <div className={styles.questionHeader}>
                <h3>Насколько быстро вы получаете нужный результат?</h3>

                {errors.speedRating && (
                  <span className={styles.errorText}>Поставьте оценку</span>
                )}
              </div>

              <Controller
                name="speedRating"
                control={control}
                rules={{
                  validate: (value) => value > 0 || "Поставьте оценку",
                }}
                render={({ field }) => (
                  <StarRating
                    value={field.value}
                    max={5}
                    error={!!errors.speedRating}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>

            <div className={styles.question}>
              <h3>Есть ли что-то, что мешает использовать сервис на 100%?</h3>

              <Controller
                name="hasBlockers"
                control={control}
                render={({ field }) => (
                  <TextField
                    select
                    fullWidth
                    value={field.value}
                    onChange={field.onChange}
                    className={styles.select}
                    sx={textFieldSx}
                  >
                    <MenuItem value="">Выберите ответ</MenuItem>
                    <MenuItem value="yes">Да</MenuItem>
                    <MenuItem value="no">Нет</MenuItem>
                  </TextField>
                )}
              />
            </div>

            <div className={styles.question}>
              <h3>Что именно?</h3>

              <Controller
                name="blockerText"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    minRows={3}
                    disabled={isBlockerTextDisabled}
                    placeholder={
                      isBlockerTextDisabled
                        ? "Поле станет доступным после выбора «Да»"
                        : "Опишите, что мешает работе"
                    }
                    sx={textFieldSx}
                  />
                )}
              />
            </div>

            <div className={styles.question}>
              <div className={styles.questionHeader}>
                <h3>Готовы ли вы рекомендовать Наймус коллегам?</h3>

                {errors.recommendScore && (
                  <span className={styles.errorText}>Поставьте оценку</span>
                )}
              </div>

              <Controller
                name="recommendScore"
                control={control}
                rules={{
                  validate: (value) => value > 0 || "Поставьте оценку",
                }}
                render={({ field }) => (
                  <StarRating
                    value={field.value}
                    max={10}
                    error={!!errors.recommendScore}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>

            <div className={styles.question}>
              <h3>Хотите оставить комментарий или идею?</h3>

              <Controller
                name="comment"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder="Напишите комментарий или идею"
                    sx={textFieldSx}
                  />
                )}
              />
            </div>
          </section>
        </div>

        <div className={styles.actions}>
          <Button
            btnText={isPending ? "Отправляем..." : "Отправить отзыв"}
            typeBtn="submit"
            colorVariant="primary"
            isDisabled={isPending}
          />
        </div>
      </form>
    </div>
  );
};

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#FFFFFF",

    "& fieldset": {
      borderColor: "rgba(164, 187, 199, 0.5)",
    },

    "&:hover fieldset": {
      borderColor: "rgba(164, 187, 199, 0.8)",
    },

    "&.Mui-focused fieldset": {
      borderColor: "#1FA1E5",
    },

    "&.Mui-disabled": {
      backgroundColor: "rgba(164, 187, 199, 0.12)",
    },
  },
  "& .MuiInputBase-input": {
    color: "#262626",
    fontSize: "14px",
  },

  "& .MuiInputBase-input::placeholder": {
    color: "#A4BBC7",
    opacity: 1,
  },

  "& .MuiSelect-select": {
    color: "#262626",
    fontSize: "14px",
  },
};
